import { action, computed, observable, reaction, comparer } from 'mobx';
import { PRODUCT_STORAGE_KEY } from '@link/share/constants';
import {
  Product as ProductType,
  ActivityCalendar as ActivityCalendarType
} from '@link/shibuya-sky/services/types';

const storage = sessionStorage;

// Returned for any productId that hasn't loaded yet, so a page reading
// getProduct(routeId) never sees a different, previously-viewed product.
const EMPTY_PRODUCT: ProductType = {};

export class Product {
  // Products keyed by productId. A page reads getProduct(routeId), so it only
  // ever sees the product for its own route — navigating to a new product can
  // never surface the previously-viewed one before the new data arrives.
  @observable accessor productsById = new Map<string, ProductType>();
  // Most recently loaded productId, backing the `product` getter for consumers
  // that want "the current product" without a route id (layout language
  // switcher, booking confirm analytics).
  @observable accessor currentProductId = '';
  @observable accessor activityCalendar: ActivityCalendarType = {};

  constructor() {
    this.hydrate();
    this.initPersistence();
  }

  @action
  private hydrate() {
    try {
      const raw = storage.getItem(PRODUCT_STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as {
          productsById?: Record<string, ProductType>;
          currentProductId?: string;
        };
        if (data.productsById) {
          this.productsById = new Map(Object.entries(data.productsById));
        }
        if (data.currentProductId) {
          this.currentProductId = data.currentProductId;
        }
      }
    } catch {
      void 0;
    }
  }

  private initPersistence() {
    // Persist only the current product, not the whole in-memory cache, so the
    // stored payload stays bounded to one product (matching the footprint
    // before products were keyed). The in-memory Map still caches every product
    // viewed this session; getInitialData refetches on mount regardless.
    reaction(
      () => ({
        currentProductId: this.currentProductId,
        currentProduct: this.productsById.get(this.currentProductId)
      }),
      ({ currentProductId, currentProduct }) => {
        storage.setItem(
          PRODUCT_STORAGE_KEY,
          JSON.stringify({
            currentProductId,
            productsById: currentProduct
              ? { [currentProductId]: currentProduct }
              : {}
          })
        );
      },
      { equals: comparer.structural, delay: 200 }
    );
  }

  // Route-safe read: the loaded product for `productId`, or empty if it hasn't
  // loaded yet.
  getProduct(productId: string): ProductType {
    return this.productsById.get(productId) ?? EMPTY_PRODUCT;
  }

  @action
  setProduct(productId: string, product: ProductType) {
    this.productsById.set(productId, product);
    this.currentProductId = productId;
  }

  @action
  setActivityCalendar(activityCalendar: ActivityCalendarType) {
    this.activityCalendar = activityCalendar;
  }

  // The current product (most recently loaded). For consumers without a route
  // id; route-bound pages should use getProduct(routeId) instead.
  @computed
  get product() {
    return this.getProduct(this.currentProductId);
  }

  @computed
  get productContent() {
    return this.product?.productContent;
  }

  @computed
  get activity() {
    return this.productContent?.activity;
  }
}

export default new Product();
